// CvCadLogin.tsx — /cv-cad-login route.
//
// AutoCAD's cv-cad plugin opens this URL in the user's default browser with a
// machine_id query parameter. We require the user to be signed in (or sign in
// here), then mint a short-lived single-use pickup code bound to (user_id,
// machine_id). The browser shows the pickup code (not a long-lived token) and
// attempts a cv-cad:// deep-link handoff carrying only the code.
//
// cv-cad exchanges the pickup code for the actual device token at
// POST /device-tokens-redeem (sibling function with verify_jwt = false — the
// pickup code itself is the bearer for that single round-trip). The raw
// device token is never present in any URL, browser history, screen-share,
// or screenshot.
//
// RISK-006 / CV-639.

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import SignInPanel from '../auth/SignInPanel';
import { supabase, SUPABASE_FN_URL } from '../auth/supabase';

interface IssuedPickup { pickup_code: string; expires_at: string; }

export default function CvCadLogin() {
  const { session, loading } = useAuth();
  const [machineId, setMachineId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [pickup, setPickup] = useState<IssuedPickup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setMachineId(params.get('machine_id'));
  }, []);

  async function issue() {
    if (!session || !machineId) return;
    try {
      setBusy(true); setError(null);
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) throw new Error('session lost');
      const res = await fetch(`${SUPABASE_FN_URL}/device-tokens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${s.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ machine_id: machineId, label: label || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: IssuedPickup = await res.json();
      setPickup(data);
      // Deep-link handoff carries the pickup code only — never the raw token.
      // If cv-cad isn't registered for cv-cad:// the browser silently does
      // nothing and the user types the visible pickup code into the plugin.
      const deepLink = `cv-cad://auth?code=${encodeURIComponent(data.pickup_code)}`;
      window.location.href = deepLink;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="cv-cadlogin-page"><p>Loading…</p></div>;
  }

  if (!machineId) {
    return (
      <div className="cv-cadlogin-page">
        <div className="cv-cadlogin-card">
          <h1>Missing machine identifier</h1>
          <p>
            This page is opened by ConstructiVision-CAD with a machine ID in the URL.
            If you arrived here some other way, open ConstructiVision-CAD and use its
            sign-in command.
          </p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="cv-cadlogin-page">
        <div className="cv-cadlogin-card">
          <h1>Sign in to authorize this workstation</h1>
          <p>Machine ID: <span className="machine">{machineId}</span></p>
          <SignInPanel
            title="Sign in"
            subtitle="Once you're signed in we'll hand a short pickup code to ConstructiVision-CAD."
          />
        </div>
      </div>
    );
  }

  if (pickup) {
    return (
      <div className="cv-cadlogin-page">
        <div className="cv-cadlogin-card">
          <h1>Pickup code ready</h1>
          <p>ConstructiVision-CAD should have received this code automatically. If it didn't,
            type this code into the plugin's "Sign In" dialog before it expires:</p>
          <div className="cv-cadlogin-token">{pickup.pickup_code}</div>
          <p className="cv-cadlogin-warning">
            This code expires at {new Date(pickup.expires_at).toLocaleTimeString()} and can be
            used exactly once. The actual device credential never leaves the secure exchange —
            it's minted directly inside ConstructiVision-CAD when the code is redeemed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-cadlogin-page">
      <div className="cv-cadlogin-card">
        <h1>Authorize this workstation</h1>
        <p>Signed in as <strong>{session.user.email ?? session.user.id}</strong>.</p>
        <p>Machine ID: <span className="machine">{machineId}</span></p>
        <label style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
          Label (optional):
          <input
            type="text"
            placeholder="e.g., Office laptop"
            value={label}
            onChange={e => setLabel(e.target.value)}
            style={{ display: 'block', marginTop: 4, padding: '8px 10px', borderRadius: 6,
                     border: '1px solid #d0d4dc', width: '100%', fontSize: 14 }}
          />
        </label>
        <button
          className="cv-btn cv-btn-primary"
          style={{ marginTop: 16, width: '100%' }}
          disabled={busy}
          onClick={issue}
        >
          {busy ? 'Generating…' : 'Authorize this workstation'}
        </button>
        {error && <div className="signin-error" role="alert">{error}</div>}
      </div>
    </div>
  );
}
